import { useState } from 'react';
import type { ViewResolution } from '@ayumia/contracts';
import { Btn } from '../design-system/primitives';
import { CommandDialog } from '../views/CommandDialog';
import { useLoad } from '../views/useLoad';
import { useAppServices } from '../views/ViewContext';
import type { ObjectSummary } from '../views/format';
import { relationLabel } from './graph-data';
import type { WireRef, WireTraversal } from './graph-model';

/**
 * Chronologie de la traçabilité (maquette « Graphe — traçabilité » validée le 2026-10-15) : les faits qui
 * ont écrit les liens, regroupés par jour, avec leurs relations. Elle ne lit que `valid_from` des arêtes
 * (graph-traversal 1.2.0) : un lien porté par un champ n'a pas de date et n'y figure pas.
 */
export function TraceTimeline({ traversal, summaries }: { traversal: WireTraversal; summaries: ReadonlyMap<string, ObjectSummary | null> }) {
  const days = new Map<string, { label: string; relations: Set<string>; count: number }>();
  for (const edge of traversal.edges) {
    if (edge.valid_from === undefined) continue;
    const at = new Date(edge.valid_from);
    if (Number.isNaN(at.getTime())) continue;
    // Jour civil au fuseau du navigateur : la chronologie se lit là où l'on travaille.
    const key = `${at.getFullYear()}-${String(at.getMonth() + 1).padStart(2, '0')}-${String(at.getDate()).padStart(2, '0')}`;
    const day = days.get(key) ?? { label: at.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }), relations: new Set<string>(), count: 0 };
    day.relations.add(relationLabel(summaries, edge));
    day.count += 1;
    days.set(key, day);
  }
  if (days.size === 0) return null;
  const ordered = [...days.entries()].sort(([a], [b]) => a.localeCompare(b));
  return (
    <section className="ay-card ay-graph__timeline" aria-label="Chronologie">
      <strong>Chronologie</strong>
      <ol>
        {ordered.map(([key, day]) => (
          <li key={key}>
            <span className="ay-graph__timeline-date">{day.label}</span>
            <span aria-hidden="true" className="ay-graph__timeline-dot" />
            <span>
              {[...day.relations].join(', ')}
              <span className="ay-graph__muted"> · {day.count} lien{day.count > 1 ? 's' : ''}</span>
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}

const RECALLS_VIEW = 'core.view.recalls';
const PROPOSE_RECALL = 'ProposeRecall';

/**
 * « Préparer un rappel » depuis un lot tracé (ADR-112) : la proposition part avec le lot de départ ; le
 * motif et le danger se saisissent. Le périmètre est recalculé par le serveur sur l'état autoritaire,
 * jamais repris de l'affichage ; la décision reste à quatre yeux. Sans droit de lire les rappels, rien.
 */
export function PrepareRecall({ start }: { start: WireRef }) {
  const services = useAppServices();
  const [open, setOpen] = useState(false);
  const [resolution] = useLoad<ViewResolution | null>(`graph-recalls:${RECALLS_VIEW}`, async () => {
    try {
      return (await services.client.query<ViewResolution>('GetView', { view_ref: RECALLS_VIEW })).data;
    } catch {
      return null;
    }
  });
  if (start.type !== 'Lot') return null;
  const propose = resolution.status === 'ready' ? resolution.value?.commands?.find((command) => command.command_name === PROPOSE_RECALL) : undefined;
  if (propose === undefined) return null;
  return (
    <>
      <Btn variant="dark" onClick={() => setOpen(true)}>
        Préparer un rappel
      </Btn>
      {open && (
        <CommandDialog
          command={propose}
          prefilled={{ lot_id: start.id }}
          target={null}
          intro={<p className="ay-text ay-text--muted">Le périmètre du rappel est recalculé sur l’état autoritaire, pas repris de l’affichage ; une seconde personne décide.</p>}
          onClose={() => setOpen(false)}
          onDone={() => {
            setOpen(false);
            services.navigate({ kind: 'view', view_ref: RECALLS_VIEW });
          }}
        />
      )}
    </>
  );
}
